import React, { useEffect, useRef, useState } from 'react'
import FocusableKeysTS from '../FocusableButton/FocusableButtonTS'
import { setKeyProperties } from './utils'
import { ToggleKeyType, AlphaCollection, NumericCollection } from './types'
import type { KeyboardKeysProps } from './types'
import {
  FocusContext,
  useFocusable,
  setFocus,
  FocusableComponentLayout,
} from '@noriginmedia/norigin-spatial-navigation'
import cx from 'classnames'
import tts from '../../lib/TTS'
import { useSidePanel } from '../SidePanel/SidePanelProvider'

const alphaKeys = Object.values(AlphaCollection)
const numericKeys = Object.values(NumericCollection)

const PARENT_COMPONENT = 'KEYBOARD_BUTTONS_COMPONENT'
const FIRST_ALPHA_KEY = 'ALPHA_KEY_1'
const FIRST_NUMERIC_KEY = 'NUMERIC_KEY_1'

function KeyboardKeys({
  keyboardToggleKey,
  onEnterPress,
  onArrowPress,
}: KeyboardKeysProps) {
  const { ref, focusKey } = useFocusable({
    focusKey: PARENT_COMPONENT,
    onFocus: (_arg1, _arg2, details) => {
      if (
        details &&
        details.event &&
        details.event.type === 'keydown' &&
        (details.event as KeyboardEvent).key === 'ArrowDown'
      ) {
        forceFocus &&
          setFocus(
            keyboardToggleKey === ToggleKeyType.alpha
              ? FIRST_ALPHA_KEY
              : FIRST_NUMERIC_KEY
          )

        forceFocus && setForceFocus(false)
      }
    },
  })
  const { isSidePanelOpened } = useSidePanel()
  const oldKeyboardToggleKey = useRef<ToggleKeyType>(keyboardToggleKey)
  const [forceFocus, setForceFocus] = useState(false)

  useEffect(() => {
    if (oldKeyboardToggleKey.current !== keyboardToggleKey) {
      oldKeyboardToggleKey.current = keyboardToggleKey
      setForceFocus(true)
    }
  }, [keyboardToggleKey])

  useEffect(() => {
    if (!isSidePanelOpened) {
      tts.pushTextToRead('Enter keywords to perform a search')
      setFocus(
        keyboardToggleKey === ToggleKeyType.alpha
          ? FIRST_ALPHA_KEY
          : FIRST_NUMERIC_KEY
      )
    }
  }, [isSidePanelOpened])

  const handleKeyFocus = (
    _: FocusableComponentLayout,
    props: { keyTTS: string }
  ) => {
    tts.stop()
    tts.addTextToRead(`Focused on ${props.keyTTS}`)
  }

  const handleKeyEnter = (props: {
    keyString: AlphaCollection | NumericCollection
    keyTTS: string
  }) => {
    const { keyString, keyTTS } = props
    tts.stop()
    tts.addTextToRead(`Selected ${keyTTS}`)
    onEnterPress({ keyString })
  }

  const buttons =
    keyboardToggleKey === ToggleKeyType.alpha ? alphaKeys : numericKeys
  return (
    <FocusContext.Provider value={focusKey}>
      <div className="keyboard" ref={ref}>
        <div
          className={cx('keyboard__buttons', {
            'keyboard__buttons--alpha':
              keyboardToggleKey === ToggleKeyType.alpha,
            'keyboard__buttons--numeric':
              keyboardToggleKey === ToggleKeyType.numeric,
          })}
        >
          {buttons.map(
            (singleKey: AlphaCollection | NumericCollection, index: number) => {
              const keyData = setKeyProperties(singleKey)
              const focusKeyId = `${keyboardToggleKey.toUpperCase()}_KEY_${index}`
              return (
                <FocusableKeysTS
                  className="keyboard__single-button"
                  key={`${keyboardToggleKey}_KEY_${index}`}
                  focusKey={focusKeyId}
                  onArrowPress={onArrowPress}
                  onEnterPress={handleKeyEnter}
                  onFocus={handleKeyFocus}
                  extraProps={{
                    keyString: singleKey,
                    index,
                    keyTTS: keyData.keyTTS,
                  }}
                >
                  {keyData.keyTxt}
                </FocusableKeysTS>
              )
            }
          )}
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default KeyboardKeys
